import React, { useEffect, useState } from 'react';
import { Sparkles, X } from 'lucide-react';
import { cn } from '../../utils/cn';

export type RoaryPageType = 'about' | 'pricing' | 'contact' | 'services' | 'custom';

export interface AddPageWithRoaryValues {
  pageType: RoaryPageType;
  customTitle?: string;
  purpose: string;
  addToNav: boolean;
}

interface AddPageWithRoaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: AddPageWithRoaryValues) => void;
  isLoading?: boolean;
}

const PAGE_TYPES: Array<{ id: RoaryPageType; label: string }> = [
  { id: 'about', label: 'About' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'contact', label: 'Contact' },
  { id: 'services', label: 'Services' },
  { id: 'custom', label: 'Custom' },
];

export const AddPageWithRoaryModal: React.FC<AddPageWithRoaryModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const [pageType, setPageType] = useState<RoaryPageType>('about');
  const [customTitle, setCustomTitle] = useState('');
  const [purpose, setPurpose] = useState('');
  const [addToNav, setAddToNav] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    setPageType('about');
    setCustomTitle('');
    setPurpose('');
    setAddToNav(true);
    setErrors({});
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!purpose.trim()) nextErrors.purpose = 'Tell Roary what this page is for.';
    if (pageType === 'custom' && !customTitle.trim()) {
      nextErrors.customTitle = 'Enter a page title.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    onSubmit({
      pageType,
      customTitle: pageType === 'custom' ? customTitle.trim() : undefined,
      purpose: purpose.trim(),
      addToNav,
    });
  };

  return (
    <div
      className={cn(
        'modal-backdrop',
        'flex items-center justify-center z-50 p-4',
        'animate-fade-in'
      )}
      onClick={e => {
        if (e.target === e.currentTarget && !isLoading) onClose();
      }}
    >
      <div className={cn('modal-content', 'max-w-md w-full', 'animate-slide-up')}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add page with Roary</h2>
            <p className="text-sm text-gray-500 mt-1">
              Roary will draft the page. You can tweak copy and photos after.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">Page type</legend>
            <div className="flex flex-wrap gap-2">
              {PAGE_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setPageType(type.id)}
                  disabled={isLoading}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                    pageType === type.id
                      ? 'bg-orange-50 border-orange-300 text-orange-800'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                  )}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </fieldset>

          {pageType === 'custom' && (
            <div>
              <label
                htmlFor="roary-custom-title"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Page title
              </label>
              <input
                id="roary-custom-title"
                type="text"
                value={customTitle}
                onChange={e => setCustomTitle(e.target.value)}
                disabled={isLoading}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="FAQ, Thank you, Get started…"
              />
              {errors.customTitle && (
                <p className="mt-1 text-sm text-red-600">{errors.customTitle}</p>
              )}
            </div>
          )}

          <div>
            <label htmlFor="roary-purpose" className="block text-sm font-medium text-gray-700 mb-1">
              Who is this for, and what should they do?
            </label>
            <textarea
              id="roary-purpose"
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              disabled={isLoading}
              rows={3}
              maxLength={280}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Parents of new students should book a trial class"
            />
            {errors.purpose && <p className="mt-1 text-sm text-red-600">{errors.purpose}</p>}
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={addToNav}
              onChange={e => setAddToNav(e.target.checked)}
              disabled={isLoading}
              className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
            />
            Add to navigation
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={isLoading} className="btn-ghost">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700"
            >
              <Sparkles size={16} />
              {isLoading ? 'Starting…' : 'Build page'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
